import React, { useId, useLayoutEffect, useRef, useState } from "react";

/**
 * Wordmark "Madjeo" en SVG : "Mad" + "je" en texte vectoriel (Inter),
 * le "o" final remplacé par le glyphe globe + satellite (agrandi par
 * rapport au texte pour bien accrocher l'œil). Largeur du texte mesurée
 * dynamiquement (getComputedTextLength) pour un positionnement exact du
 * globe quel que soit le navigateur/la police chargée.
 *
 * `height` pilote toute la taille (le SVG garde le ratio largeur/hauteur).
 */
export default function LogoWordmark({ height = 28, madColor = "#f0f6ff", className, style }) {
  const uid = useId();
  const madRef = useRef(null);
  const jeRef = useRef(null);
  const [madW, setMadW] = useState(0);
  const [jeW, setJeW] = useState(0);
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    if (madRef.current && jeRef.current) {
      setMadW(madRef.current.getComputedTextLength());
      setJeW(jeRef.current.getComputedTextLength());
      setReady(true);
    }
  }, []);

  const fontSize = height * 0.92;
  const baseline = height * 0.80;
  const capHeight = fontSize * 0.727; // hauteur des majuscules (Inter bold)
  const gap = height * 0.07;
  const globeD = capHeight * 1.12; // diamètre du globe — calé sur la hauteur des majuscules, avec léger débord optique
  const globeR = globeD / 2;
  const globeX = madW + jeW + gap;
  const globeCx = globeX + globeR;
  const globeCy = baseline + globeD * 0.028 - globeR;
  const satR = globeR / 9.5; // ratio interne du glyphe (viewBox 26x24, globe r=9.5)

  const width = globeX + globeD + height * 0.08;

  const globeGrad = `madjeo-wm-globe-${uid}`;
  const panelGrad = `madjeo-wm-panel-${uid}`;
  const accentGrad = `madjeo-wm-accent-${uid}`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={ready ? width : 0}
      height={height}
      className={className}
      style={{ display: "inline-block", overflow: "visible", transition: "width 0.1s", ...style }}
      role="img"
      aria-label="Madjeo"
    >
      <defs>
        <linearGradient id={accentGrad} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#818cf8" />
          <stop offset="100%" stopColor="#22d3ee" />
        </linearGradient>
        <radialGradient id={globeGrad} cx="38%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#7dd3fc" />
          <stop offset="45%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#4338ca" />
        </radialGradient>
        <linearGradient id={panelGrad} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a5f3fc" />
          <stop offset="100%" stopColor="#22d3ee" />
        </linearGradient>
      </defs>

      <text ref={madRef} x="0" y={baseline} fontFamily="Inter, sans-serif" fontWeight="800"
            fontSize={fontSize} fill={madColor} letterSpacing="-0.02em">Mad</text>
      <text ref={jeRef} x={madW} y={baseline} fontFamily="Inter, sans-serif" fontWeight="800"
            fontSize={fontSize} fill={`url(#${accentGrad})`} letterSpacing="-0.02em">je</text>

      {ready && (
        <g>
          {/* amorce d'orbite */}
          <path
            d={`M ${globeCx + satR * 6.4} ${globeCy - satR * 6.7}
                A ${satR * 6.9} ${satR * 6.9} 0 0 1 ${globeCx + satR * 8} ${globeCy - satR * 2.8}`}
            fill="none" stroke="#67e8f9" strokeWidth={satR * 0.9} strokeLinecap="round"
            strokeDasharray={`${satR * 0.25} ${satR * 1.5}`} opacity="0.85"
          />

          {/* globe */}
          <circle cx={globeCx} cy={globeCy} r={globeR} fill={`url(#${globeGrad})`} />
          <g stroke="#e0f2fe" strokeWidth={globeR * 0.07} fill="none" opacity="0.55">
            <ellipse cx={globeCx} cy={globeCy} rx={globeR} ry={globeR * 0.38} />
            <ellipse cx={globeCx} cy={globeCy} rx={globeR} ry={globeR * 0.72} />
            <ellipse cx={globeCx} cy={globeCy} rx={globeR * 0.38} ry={globeR} />
          </g>
          <circle cx={globeCx} cy={globeCy} r={globeR} fill="none" stroke="#1e1b4b" strokeWidth={globeR * 0.05} opacity="0.3" />
          <ellipse cx={globeCx - globeR * 0.36} cy={globeCy - globeR * 0.46} rx={globeR * 0.38} ry={globeR * 0.25} fill="#ffffff" opacity="0.22" />

          {/* satellite */}
          <g transform={`translate(${globeCx + satR * 6.4} ${globeCy - satR * 6.7}) rotate(35) scale(${satR})`}>
            <rect x="-3.4" y="-0.75" width="2.7" height="1.5" rx="0.25" fill={`url(#${panelGrad})`} stroke="#0e7490" strokeWidth="0.25" />
            <rect x="0.7" y="-0.75" width="2.7" height="1.5" rx="0.25" fill={`url(#${panelGrad})`} stroke="#0e7490" strokeWidth="0.25" />
            <rect x="-0.75" y="-1" width="1.5" height="2" rx="0.35" fill="#f1f5f9" stroke="#334155" strokeWidth="0.3" />
          </g>
        </g>
      )}
    </svg>
  );
}
